import { useState } from "react";
import { Calendar as CalendarIcon, X, Filter, ChevronDown } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Calendar } from "../../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";
import { Badge } from "../../../components/ui/badge";
import { cn } from "../../../lib/utils";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import type { Doctor, Room, AppointmentFilters, AppointmentStatusType } from "../../../shared/appointmentSchema";
import { AppointmentStatus } from "../../../shared/appointmentSchema";

interface FilterPanelProps {
  filters: AppointmentFilters;
  onFiltersChange: (filters: AppointmentFilters) => void;
  doctors: Doctor[];
  rooms: Room[];
  className?: string;
  isMobile?: boolean;
}

const statusOptions: { value: AppointmentStatusType | "all"; label: string }[] = [
  { value: "all", label: "Tất cả trạng thái" },
  { value: AppointmentStatus.PENDING, label: "Chờ xác nhận" },
  { value: AppointmentStatus.CONFIRMED, label: "Đã xác nhận" },
  { value: AppointmentStatus.CANCELLED, label: "Đã hủy" },
];

export function FilterPanel({
  filters,
  onFiltersChange,
  doctors,
  rooms,
  className,
  isMobile = false,
}: FilterPanelProps) {
  const [dateOpen, setDateOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  const activeFilterCount = [
    filters.date,
    filters.doctorId,
    filters.roomId,
    filters.status,
  ].filter(Boolean).length;

  const handleDateSelect = (date: Date | undefined) => {
    onFiltersChange({
      ...filters,
      date: date ? format(date, "yyyy-MM-dd") : undefined,
    });
    setDateOpen(false);
  };

  const handleDoctorChange = (value: string) => {
    onFiltersChange({
      ...filters,
      doctorId: value === "all" ? undefined : value,
    });
  };

  const handleRoomChange = (value: string) => {
    onFiltersChange({
      ...filters,
      roomId: value === "all" ? undefined : value,
    });
  };

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value === "all" ? undefined : (value as AppointmentStatusType),
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  const selectedDate = filters.date ? new Date(filters.date) : undefined;

  const FilterContent = (
    <div className={cn("space-y-4", isMobile ? "p-4" : "")}>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Ngày khám</label>
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !selectedDate && "text-muted-foreground"
              )}
              data-testid="button-filter-date"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate
                ? format(selectedDate, "dd/MM/yyyy", { locale: vi })
                : "Chọn ngày"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              locale={vi}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Bác sĩ</label>
        <Select
          value={filters.doctorId || "all"}
          onValueChange={handleDoctorChange}
        >
          <SelectTrigger data-testid="select-filter-doctor">
            <SelectValue placeholder="Tất cả bác sĩ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả bác sĩ</SelectItem>
            {doctors.map((doctor) => (
              <SelectItem key={doctor.id} value={doctor.id}>
                {doctor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Phòng khám</label>
        <Select
          value={filters.roomId || "all"}
          onValueChange={handleRoomChange}
        >
          <SelectTrigger data-testid="select-filter-room">
            <SelectValue placeholder="Tất cả phòng" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả phòng</SelectItem>
            {rooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Trạng thái</label>
        <Select
          value={filters.status || "all"}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger data-testid="select-filter-status">
            <SelectValue placeholder="Tất cả trạng thái" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          onClick={handleClearFilters}
          className="w-full text-muted-foreground"
          data-testid="button-clear-filters"
        >
          <X className="mr-2 h-4 w-4" />
          Xóa bộ lọc ({activeFilterCount})
        </Button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className={cn("border-b bg-card", className)}
      >
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between p-4 rounded-none"
            data-testid="button-toggle-filters"
          >
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span>Bộ lọc</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
            </div>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>{FilterContent}</CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Bộ lọc
        </h3>
        {activeFilterCount > 0 && (
          <Badge variant="secondary" className="text-xs">
            {activeFilterCount}
          </Badge>
        )}
      </div>
      {FilterContent}
    </div>
  );
}
